import { useEffect, useState } from 'react';
import {
  BOARD_STATUSES,
  PRIORITIES,
  PRIORITY_LABELS,
  STATUS_LABELS,
  type Task,
  type TaskPatch,
  type TaskPriority,
  type TaskStatus,
} from '../../api/organizer';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { RemindAtField } from './fields/RemindAtField';
import { dueLabel } from './taskUtils';

interface TaskDetailPanelProps {
  task: Task | undefined;
  onClose: () => void;
  onPatch: (taskId: string, patch: TaskPatch) => void;
  onDelete: (taskId: string) => void;
}

/**
 * Full edit for one task, opened by the chevron on a card or by a deep link.
 *
 * Fields commit on blur rather than behind a Save button: everything else on
 * this page saves as you go, and a modal that needs saving while cards do not is
 * the kind of inconsistency that loses edits.
 */
export function TaskDetailPanel({ task, onClose, onPatch, onDelete }: TaskDetailPanelProps) {
  const [notes, setNotes] = useState('');

  useEffect(() => {
    setNotes(task?.notes ?? '');
  }, [task?.id, task?.notes]);

  if (!task) return null;

  const meta = task.sourceMeta;

  return (
    <Modal isOpen onClose={onClose} title="Task" size="xl">
      <div className="space-y-5">
        <div>
          <label className="mb-1 block text-body-xs font-medium text-text-secondary">Title</label>
          <input
            defaultValue={task.title}
            key={`${task.id}-title`}
            onBlur={(e) => {
              const next = e.target.value.trim();
              if (next && next !== task.title) onPatch(task.id, { title: next });
            }}
            className="w-full rounded-lg border border-border-light bg-surface-primary px-3 py-2 text-body-sm outline-none focus:border-text-primary"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-body-xs font-medium text-text-secondary">Status</label>
            <select
              value={task.status}
              onChange={(e) => onPatch(task.id, { status: e.target.value as TaskStatus })}
              className="w-full rounded-lg border border-border-light bg-surface-primary px-3 py-2 text-body-sm outline-none focus:border-text-primary"
            >
              {BOARD_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {STATUS_LABELS[status]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-body-xs font-medium text-text-secondary">Priority</label>
            <select
              value={task.priority}
              onChange={(e) => onPatch(task.id, { priority: e.target.value as TaskPriority })}
              className="w-full rounded-lg border border-border-light bg-surface-primary px-3 py-2 text-body-sm outline-none focus:border-text-primary"
            >
              {PRIORITIES.map((priority) => (
                <option key={priority} value={priority}>
                  {PRIORITY_LABELS[priority]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-body-xs font-medium text-text-secondary">
              Due {task.dueDate && <span className="text-text-muted">· {dueLabel(task.dueDate)}</span>}
            </label>
            <input
              type="date"
              value={task.dueDate ?? ''}
              onChange={(e) => onPatch(task.id, { dueDate: e.target.value || null })}
              className="w-full rounded-lg border border-border-light bg-surface-primary px-3 py-2 text-body-sm outline-none focus:border-text-primary"
            />
          </div>
        </div>

        <RemindAtField
          value={task.remindAt}
          onChange={(remindAt) => onPatch(task.id, { remindAt })}
        />

        <div>
          <label className="mb-1 block text-body-xs font-medium text-text-secondary">Notes</label>
          <textarea
            value={notes}
            rows={5}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => {
              if (notes !== (task.notes ?? '')) onPatch(task.id, { notes: notes || null });
            }}
            placeholder="Context, links, next step…"
            className="w-full resize-y rounded-lg border border-border-light bg-surface-primary px-3 py-2 text-body-sm outline-none focus:border-text-primary"
          />
        </div>

        {task.source === 'fathom' && meta && (
          <div className="rounded-lg border border-border-light bg-surface-secondary p-3">
            <p className="text-body-xs font-medium text-text-secondary">
              From a call{meta.meetingTitle ? `: ${meta.meetingTitle}` : ''}
            </p>
            {meta.quote && (
              <blockquote className="mt-1.5 border-l-2 border-border pl-2 text-body-xs italic text-text-tertiary">
                “{meta.quote}”
                {meta.speaker && <span className="not-italic"> — {meta.speaker}</span>}
              </blockquote>
            )}
            {meta.shareUrl && (
              <a
                href={meta.shareUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-block text-body-xs font-medium text-accent-dark hover:underline"
              >
                Open the recording{meta.timestamp ? ` at ${meta.timestamp}` : ''} →
              </a>
            )}
          </div>
        )}

        <div className="flex items-center justify-between border-t border-border-light pt-4">
          <button
            type="button"
            onClick={() => {
              onDelete(task.id);
              onClose();
            }}
            className="text-body-sm font-medium text-error hover:text-error-dark"
          >
            Delete task
          </button>
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
}
